import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, ZigButton, ZigTypography } from '@ui/index';
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { usePaybisRequestStatusQuery } from '../../../../../../apis/user/api';
import { PAYBIS_STATUS_CHECK_INTERVAL } from './constants';
import { useNavigate } from 'react-router-dom';
import {
  ROUTE_MAIN_MARKETPLACE,
  ROUTE_MY_BALANCES_TRANSACTIONS,
} from '../../../../../../routes';
import { useCloseAllZModals } from '../../../../../../components/ZModal/use';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useCoinBalances } from '../../../../../../apis/coin/use';

// ok bois. Here we're kinda supposed to have 4 statuses
// 1. waiting for deposit to arrive. means !!requestError.
// 2. payment successful, funds not arriveed yet
// 3. payment successful, funds arrived
// 4. expired/rejected/anuthing - not supported kidna.
enum PaybisDepositStatus {
  Error = -1,
  Waiting = 0,
  OnChain = 1,
  PaymentOkDepositWaiting = 2,
  PaymentOkDepositOk = 3,
  Denied,
}

function PaybisRequestStatus({
  requestId,
  reset,
}: {
  requestId: string;
  reset: () => void;
}): React.ReactElement {
  const { t } = useTranslation(['deposit-crypto']);
  const navigate = useNavigate();

  const { data: requestStatus, error: requestError } =
    usePaybisRequestStatusQuery(requestId, {
      skip: !requestId,
      pollingInterval: PAYBIS_STATUS_CHECK_INTERVAL,
    });

  const { data: coins } = useCoinBalances({
    pollingInterval: PAYBIS_STATUS_CHECK_INTERVAL,
  });
  const balance = coins?.USDT?.balanceFree;

  const [firstBalance, setFirstBalance] = useState(null);
  useEffect(() => {
    if (coins && firstBalance === null) {
      setFirstBalance(balance);
    }
  }, [balance]);

  const closeAll = useCloseAllZModals();

  const status = useMemo(() => {
    if (requestError) return PaybisDepositStatus.Error;
    else if (!requestStatus?.paidAt) return PaybisDepositStatus.Waiting;
    else if (!requestStatus.completedAt) return PaybisDepositStatus.OnChain;
    else if (firstBalance < balance)
      return PaybisDepositStatus.PaymentOkDepositWaiting;
    else return PaybisDepositStatus.PaymentOkDepositOk;
  }, [requestStatus, requestError]);

  const steps = useMemo(
    () => [
      t('buy-crypto-modal.steps.fiat'),
      t('buy-crypto-modal.steps.onchain-deposit'),
      t('buy-crypto-modal.steps.success'),
    ],
    [t],
  );

  return (
    <Box sx={{ textAlign: 'center', pt: 2 }}>
      <Stepper
        activeStep={status === PaybisDepositStatus.Error ? 0 : status}
        alternativeLabel
        sx={{ mb: 4 }}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '.simple-loader': {
                  display: 'inline-block',
                  marginRight: '8px',
                  verticalAlign: 'middle',
                },
              }}
              {...(status === PaybisDepositStatus.Error && index === 0
                ? { error: true }
                : {})}
            >
              {index === status && <Loader width={16} height={16} />}
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {status === PaybisDepositStatus.Error && (
        <>
          <ZigTypography component={'p'} sx={{ textAlign: 'center', mb: 4 }}>
            {t('buy-crypto-modal.payment-failed', { message: '' })}
          </ZigTypography>
          <ZigButton
            size={'large'}
            variant={'outlined'}
            onClick={reset}
            startIcon={<ArrowBackIosIcon color={'secondary'} />}
          >
            {t('buy-crypto-modal.cancel-request')}
          </ZigButton>
        </>
      )}

      {status === PaybisDepositStatus.PaymentOkDepositWaiting && (
        <>
          <ZigTypography component={'p'} sx={{ textAlign: 'center' }}>
            {t('buy-crypto-modal.payment-ok-deposit-nok')}
          </ZigTypography>
        </>
      )}

      {status === PaybisDepositStatus.Waiting && (
        <>
          <ZigTypography component={'p'} sx={{ textAlign: 'center' }}>
            {t('buy-crypto-modal.payment-waiting')}
          </ZigTypography>
        </>
      )}

      {status === PaybisDepositStatus.OnChain && (
        <>
          <ZigTypography component={'p'} sx={{ textAlign: 'center' }}>
            {t('buy-crypto-modal.payment-ok-deposit-tx-pending')}
          </ZigTypography>
        </>
      )}

      {status === PaybisDepositStatus.PaymentOkDepositOk && (
        <>
          <ZigTypography component={'p'} sx={{ textAlign: 'center', mb: 4 }}>
            {t('buy-crypto-modal.payment-ok-deposit-ok')}
          </ZigTypography>

          <ZigButton
            size='large'
            sx={{ mx: 2, my: 1 }}
            variant={'contained'}
            onClick={() => {
              navigate(ROUTE_MAIN_MARKETPLACE);
              closeAll();
            }}
          >
            {t('buy-crypto-modal.post-buy-mental-balances')}
          </ZigButton>
          <ZigButton
            size='large'
            sx={{ mx: 2, my: 1 }}
            variant={'outlined'}
            onClick={() => {
              navigate(ROUTE_MY_BALANCES_TRANSACTIONS);
              closeAll();
            }}
          >
            {t('buy-crypto-modal.post-buy-cta-balances')}
          </ZigButton>
        </>
      )}
    </Box>
  );
}

export default PaybisRequestStatus;
